import { apiClient, ApiResponse, User, LoginRequest, LoginResponse } from './apiClient';
import { DEBUG } from '@/config/api-config';

// Authentication service following the integration guide
export class AuthService {
  // Login with phone and password
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    // Use phone as-is without validation
    const phone = credentials.phone;

    if (DEBUG) {
      console.log('🔐 Login attempt:', {
        phone: phone
      });
    }

    try {
      const response = await apiClient.login({
        phone: phone,
        password: credentials.password
      });

      if (DEBUG) {
        console.log('🔐 Login response:', {
          success: response.success,
          hasData: !!response.data,
          hasUser: !!(response.data?.user),
          message: response.message
        });
      }

      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('🔐 Login error:', error);
      }
      throw error;
    }
  }

  // Register new user with role-based endpoints
  async register(userData: {
    name: string;
    phone: string;
    email: string;
    password: string;
    language?: 'en' | 'ar';
    role: 'customer' | 'staff' | 'admin';
  }): Promise<ApiResponse<LoginResponse>> {
    // Use phone as-is without validation
    const registrationData = {
      name: userData.name,
      phone: userData.phone,
      email: userData.email,
      password: userData.password,
      language: userData.language || 'en',
    };

    if (DEBUG) {
      console.log('🔐 Auth Service Registration:', {
        role: userData.role,
        phone: formattedPhone,
        name: userData.name
      });
    }

    // Use role-specific registration endpoints
    switch (userData.role) {
      case 'customer':
        return apiClient.registerCustomer(registrationData);
      case 'staff':
        return apiClient.registerStaff(registrationData);
      case 'admin':
        return apiClient.registerAdmin(registrationData);
      default:
        return apiClient.register(registrationData); // Default to customer
    }
  }

  // Logout current user
  async logout(): Promise<void> {
    await apiClient.logout();
  }

  // Get current authenticated user
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.getCurrentUser();
  }

  // Refresh authentication token
  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    const response = await apiClient.refreshToken();
    
    if (response.success && response.data) {
      apiClient.setAuthToken(response.data.token);
    }
    
    return response;
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<any>> {
    return apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Update user profile
  async updateProfile(userData: {
    name?: string;
    email?: string;
    language?: 'en' | 'ar';
  }): Promise<ApiResponse<User>> {
    return apiClient.put('/auth/profile', userData);
  }

  // Request password reset
  async requestPasswordReset(phone: string): Promise<ApiResponse<any>> {
    // Use phone as-is without validation
    return apiClient.post('/auth/forgot-password', { phone: phone });
  }

  // Reset password with code
  async resetPassword(phone: string, code: string, newPassword: string): Promise<ApiResponse<any>> {
    return apiClient.post('/auth/reset-password', {
      phone,
      code,
      newPassword,
    });
  }

  // Utility methods
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  getStoredUser(): User | null {
    return apiClient.getStoredUser();
  }

  getCurrentUserRole(): 1 | 2 | 3 | null {
    const user = this.getStoredUser();
    return user?.roleId || null;
  }

  isAdmin(): boolean {
    return this.getCurrentUserRole() === 2;
  }

  isStaff(): boolean {
    return this.getCurrentUserRole() === 3;
  }

  isCustomer(): boolean {
    return this.getCurrentUserRole() === 1;
  }

  hasAdminAccess(): boolean {
    return this.isAdmin() || this.isStaff();
  }

  // Phone validation for Saudi format
  private isValidSaudiPhone(phone: string): boolean {
    // Saudi phone format: +966XXXXXXXXX (9 digits after +966)
    const saudiPhoneRegex = /^\+966[5-9]\d{8}$/;
    return saudiPhoneRegex.test(phone);
  }

  // Format phone number to Saudi format
  formatSaudiPhone(phone: string): string {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If starts with 966, add +
    if (digits.startsWith('966')) {
      return `+${digits}`;
    }
    
    // If starts with 5-9 and has 9 digits, assume it's Saudi without country code
    if (/^[5-9]\d{8}$/.test(digits)) {
      return `+966${digits}`;
    }
    
    // If starts with 0, remove it and add +966
    if (digits.startsWith('0') && digits.length === 10) {
      return `+966${digits.substring(1)}`;
    }
    
    return phone; // Return as is if can't format
  }

  // Language management
  setLanguage(language: 'en' | 'ar'): void {
    apiClient.setLanguage(language);
    
    // Update user preference on server if authenticated
    if (this.isAuthenticated()) {
      this.updateProfile({ language }).catch(console.error);
    }
  }

  getLanguage(): 'en' | 'ar' {
    return apiClient.getLanguage();
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
